import { isEmpty } from "ramda";
import { Box, Divider, Heading, Text } from "@chakra-ui/react";
import Head from "next/head";
import maplibregl from "maplibre-gl";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import NextImage from "next/image";

import Location2JsonApiEndpoint from "utils/geo/api/Location2Json";
import { EApiWatchdogFrequencyId, IWatchdog } from "utils/watchdog/types";
import NumericInterval from "utils/NumericInterval";
import { IDispositionItemConfig } from "config/dispositions";
import { useTracking } from "contexts/Tracking";
import FindWatchdogApiEndpoint from "utils/watchdog/api/FindWatchdog";
import { convertBoundsToMapbox } from "helpers/convertBoundsToMap";
import { DEFAULT_BOUNDS_CZECHIA } from "src/consts";
import WatchDog from "./components/Watchdog";
import EditForm from "./components/Form/EditForm";
import Step2Form from "./components/Form/Step2Form";
import Step3Form from "./components/Form/Step3Form";
import Bullet from "./components/Bullet";
import LocationSuggest from "components/Chakra/LocationSuggest";
import Button from "components/Chakra/Button";
import Stack from "components/Chakra/ResponsiveStack";
import { useAlertModal } from "modals/useAlertModal";
import { useWatchdogLimitModal } from "modals/useWatchdogLimitModal";
import useRouter from "hooks/useRouter";
import useApiEndpoint from "hooks/useApiEndpoint";
import useGlobalState from "hooks/useGlobalState";
import useHelperCRM from "hooks/useHelperCRM";
import useViewport from "hooks/useViewport";
import { EOrderTypeId } from "utils/payment/types";
import useWatchdogs from "hooks/useWatchdogs";
import { TCreateWatchDogInput } from "utils/watchdog/api/CreateWatchdog";

type TMaxLevel = 1 | 2 | 3;

const MAPBOX_ID = "mapbox_wd";

type NewWDType = {
  step: number;
  location: string;
  dispositions: Array<IDispositionItemConfig>;
  price: NumericInterval;
  maxLevel: TMaxLevel;
};

const DEFAULT_WD_DATA: NewWDType = {
  step: 1,
  location: "",
  dispositions: [],
  price: new NumericInterval(),
  maxLevel: 1,
};

const WatchdogPage: FC = () => {
  const [value, setValue] = useState("");
  const [canCreate, setCanCreate] = useState<{
    count: number;
    can: boolean;
  }>({ count: 0, can: false });
  const [newWatchdogStep, setNewWatchdogStep] =
    useState<NewWDType>(DEFAULT_WD_DATA);
  const refInstance = useRef<maplibregl.Map | null>(null);

  const { query } = useRouter();
  const watchdogId = query.id ? parseInt(query.id.toString()) : null;
  const { user } = useGlobalState();
  const { trackEvent } = useTracking();
  const { isMobile } = useViewport();
  const { updateUserAfterPayment } = useHelperCRM();
  const {
    getWatchdogs,
    activateDog,
    deleteDog,
    setEdit,
    edit,
    watchdogs,
    createDog,
  } = useWatchdogs();

  const { openModal: openAlertModal } = useAlertModal();

  const { openModal: openWatchdogLimitModal } = useWatchdogLimitModal();

  const { callEndpoint: findWatchdogApi } = useApiEndpoint(
    FindWatchdogApiEndpoint,
  );
  const { callEndpoint: location2JsonApi } = useApiEndpoint(
    Location2JsonApiEndpoint,
  );

  useEffect(() => {
    if (query.platba && user) {
      if (query.platba === "nezaplacena") {
        return openAlertModal({
          animation: "error",
          title: "Premium účet se nepodařilo aktivovat",
          children:
            "Platbu se bohužel nepodařilo uskutečnit. Zkuste to prosím znovu, nebo nás kontaktujte na info@ulovdomov.cz.",
          submitText: "Zkusit znovu",
        });
      }

      const orderType = query.orderType;
      const isPremiumPayment =
        !orderType ||
        [
          EOrderTypeId.PREMIUM_ACC_6_MONTHS,
          EOrderTypeId.PREMIUM_ACC_12_MONTHS,
          EOrderTypeId.PREMIUM_ACC_UNLIMITED,
        ].includes(Number(orderType));

      if (!isPremiumPayment) {
        return;
      }

      updateUserAfterPayment({
        user,
        orderType: query.orderType,
      });
      const callLocation = async () => {
        const wdData = localStorage.getItem("wd-immediate");
        if (wdData) {
          const parseWdData: TCreateWatchDogInput = JSON.parse(wdData);
          if (!parseWdData.filter.bounds) {
            const { data: locationData } = await location2JsonApi({
              location: parseWdData.location,
            });
            parseWdData.filter.bounds = locationData?.bounds;
            if (!parseWdData.email) {
              parseWdData.email = user.email;
            }
          }
          createDog(parseWdData);
          localStorage.removeItem("wd-immediate");
        }

        return openAlertModal({
          animation: "check",
          title: "Stali jste se prémiovým uživatelem",
          children: "Pes už je na lovu!",
          submitText: "Paráda",
        });
      };

      callLocation();
    }
  }, [user]);

  useEffect(() => {
    if (!value || newWatchdogStep.step !== 1) {
      return;
    }
    setCanCreate({ count: 0, can: false });

    const mapInst = new maplibregl.Map({
      boxZoom: true,
      interactive: true,
      container: MAPBOX_ID, // container id
      style: "https://tiles.ulovdomov.cz/styles/basic-preview/style.json",
      bounds: convertBoundsToMapbox(DEFAULT_BOUNDS_CZECHIA),
    });

    mapInst.on("load", () => {
      location2JsonApi({ location: value }).then((x) => {
        if (x.data?.bounds) {
          const mapBounds = convertBoundsToMapbox(x.data.bounds);
          mapBounds &&
            mapInst.fitBounds(mapBounds, {
              padding: 20,
            });

          // @ts-expect-error jebat stejně je be shit
          findWatchdogApi({ watchdogFilter: { bounds: x.data.bounds } })
            .then((res) => {
              setCanCreate({
                can: (res?.data?.count || 0) < 500,
                count: res.data?.count || 0,
              });
            })
            .catch(() => {
              setCanCreate({ count: 0, can: false });
            });

          const bounds = x.data.bounds;

          mapInst.addSource("wd-layer", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: [
                {
                  id: "wd-layout",
                  type: "Feature",
                  properties: {},
                  geometry: {
                    type: "Polygon",
                    coordinates: [
                      [
                        [bounds.northEast.lng, bounds.southWest.lat], // NW
                        [bounds.southWest.lng, bounds.southWest.lat], // SW
                        [bounds.southWest.lng, bounds.northEast.lat], // SE
                        [bounds.northEast.lng, bounds.northEast.lat], // NE
                        [bounds.northEast.lng, bounds.southWest.lat], // NW
                      ],
                    ],
                  },
                },
              ],
            },
          });

          mapInst.addLayer({
            id: "park-boundary",
            type: "fill",
            source: "wd-layer",
            paint: {
              "fill-color": "#e74c3c",
              "fill-opacity": 0.3,
            },
            filter: ["==", "$type", "Polygon"],
          });

          mapInst.addLayer({
            id: "states-layer-outline",
            type: "line",
            source: "wd-layer",
            paint: {
              "line-color": "#e74c3c",
              "line-opacity": 0.5,
              "line-width": 2,
            },
            filter: ["==", "$type", "Polygon"],
          });
        }
      });

      refInstance.current = mapInst;
    });
  }, [value, newWatchdogStep.step, location2JsonApi, findWatchdogApi]);

  useEffect(() => {
    getWatchdogs();
  }, [getWatchdogs]);

  useEffect(() => {
    if (watchdogId) {
      const myDog = watchdogs.find((w) => w.id === watchdogId);
      myDog && setEdit(myDog);
    }
  }, [setEdit, watchdogId, watchdogs]);

  useEffect(() => {
    if (!canCreate.can && canCreate?.count !== 0) {
      trackEvent({
        "dl.eCat": "watchdog",
        "dl.eAct": "too_broad_popup",
        "dl.eLab": "watchdog_page",
      });
      openWatchdogLimitModal();
    }
  }, [canCreate, openWatchdogLimitModal, trackEvent]);

  const handleDeleteWatchdog = (id: number): void => {
    openAlertModal({
      title: "Chcete Hlídacího psa smazat?",
      submitText: "ANO, smazat",
      cancelText: "NE, nemazat",
      onSubmit: () =>
        deleteDog(
          id,
          () =>
            openAlertModal({
              animation: "check",
              title: "Změny byly uloženy",
              submitText: "Děkuji",
            }),
          watchdogId != null,
        ),
    });
  };

  const mappedWatchdogs = useMemo(
    () =>
      watchdogs.reduce(
        (prev, next) => {
          if (next.frequencyId === EApiWatchdogFrequencyId.NEVER) {
            prev.stoppedWatchdogs.push(next as IWatchdog);
          } else {
            prev.activeWatchdogs.push(next as IWatchdog);
          }

          return prev;
        },
        {
          activeWatchdogs: [] as Array<IWatchdog>,
          stoppedWatchdogs: [] as Array<IWatchdog>,
        },
      ),
    [watchdogs],
  );

  const onClickContinueButton = () => {
    if (typeof canCreate === "boolean" && !canCreate) {
      openWatchdogLimitModal();

      return;
    }

    setNewWatchdogStep((prev) => ({
      ...prev,
      step: 2,
      location: value,
      maxLevel: prev.maxLevel === prev.step ? 2 : prev.maxLevel,
    }));
  };

  return (
    <>
      <Head>
        <link
          href="https://unpkg.com/maplibre-gl@2.4.0/dist/maplibre-gl.css"
          rel="stylesheet"
        />
      </Head>
      <Box
        position="absolute"
        top="200px"
        height={["1000px", "500px", "1000px"]}
        display={{ base: "none", md: "block" }}
        zIndex="-10"
        w={["600px", "400px", "600px"]}
      >
        <NextImage
          priority
          src="/img/scenes/Watchdog/watchdog.jpg"
          alt=""
          fill
          style={{ objectFit: "contain" }}
        />
      </Box>

      <Box
        data-test="wathchDogPageContainer"
        maxW="900px"
        mt="120px"
        mb={{
          md:
            isEmpty(mappedWatchdogs.activeWatchdogs) &&
            isEmpty(mappedWatchdogs.stoppedWatchdogs)
              ? "540px"
              : "60px",
        }}
        px={{ base: "16px", md: "20px" }}
        mx="auto"
      >
        <Heading as="h1" mb={8} data-test="h1heading">
          Hlídací pes najde bydlení za vás
        </Heading>
        <Text data-test="text" variant="large_2" textAlign="center" mb="3rem">
          Ty nejlepší{" "}
          <Text as="span" fontWeight="bold" fontSize="inherit">
            pronájmy mizí z trhu během prvních pár hodin.{" "}
          </Text>
          Stačí psa pustit z boudy a pravidelně vám přinese nové nabídky.
        </Text>
        {newWatchdogStep.step === 1 && !edit && (
          <Stack justify="center" direction="column" maxW="600px" m="0 auto">
            <Stack
              borderRadius="6px"
              borderWidth="1px"
              borderStyle="solid"
              borderColor="ud-grey-300"
              background="ud-white"
              p="0.25rem"
              direction="row"
              boxShadow={{
                base: "unset",
                md: "0 0 0 5px rgba(235, 235, 235, 1)",
              }}
            >
              <Stack flex={1} data-test="inputForAdress">
                <LocationSuggest
                  locationValue={value}
                  onLocationSelect={setValue}
                  changeInputOnPick
                  defaultSearchLocation={value}
                  onClear={() => setValue("")}
                  placeholder="Zadej město, adresu nebo PSČ"
                />
              </Stack>
              {value && (
                <Box display={{ base: "none", md: "flex" }}>
                  <Button
                    data-test="continueButton"
                    trackEventPayload={{
                      "dl.eCat": "landing",
                      "dl.eAct": "watchdog.clicked",
                      "dl.eLab": "WD 1. krok lokalita",
                    }}
                    disabled={!canCreate.can}
                    type="submit"
                    onClick={() => onClickContinueButton()}
                    w="fit-content"
                    h="100%"
                  >
                    Pokračovat
                  </Button>
                </Box>
              )}
            </Stack>
            <Stack display={{ md: "none" }}>
              <Button
                type="submit"
                onClick={() => {
                  setNewWatchdogStep((prev) => ({
                    ...prev,
                    step: 2,
                    location: value,
                    maxLevel: prev.maxLevel === prev.step ? 2 : prev.maxLevel,
                  }));
                }}
                w="100%"
              >
                Pokračovat
              </Button>
            </Stack>

            {Boolean(value) && (
              <Box
                data-test="mapOfSelectedAdress"
                id={MAPBOX_ID}
                mt="20px"
                borderRadius="10px"
                width="100%"
                height="200px"
              />
            )}
          </Stack>
        )}

        {newWatchdogStep.step === 2 && (
          <Step2Form setWatchdogStep={setNewWatchdogStep} />
        )}

        {newWatchdogStep.step === 3 && (
          <Step3Form
            watchdog={newWatchdogStep}
            onSubmit={() => {
              setNewWatchdogStep(DEFAULT_WD_DATA);
              setValue("");
              getWatchdogs();
            }}
          />
        )}

        {!edit && (
          <Stack
            direction="row"
            alignItems="center"
            mb="100px"
            mt="30px"
            justify="center"
          >
            <Bullet
              isActive
              onClick={() =>
                newWatchdogStep.step >= 1 &&
                setNewWatchdogStep((prev) => ({ ...prev, step: 1 }))
              }
            />
            <Bullet
              isActive={newWatchdogStep.maxLevel > 1}
              onClick={() =>
                newWatchdogStep.maxLevel > 1 &&
                setNewWatchdogStep((prev) => ({ ...prev, step: 2 }))
              }
            />
            <Bullet
              isActive={newWatchdogStep.maxLevel > 2}
              onClick={() =>
                newWatchdogStep.maxLevel > 2 &&
                setNewWatchdogStep((prev) => ({ ...prev, step: 3 }))
              }
            />
          </Stack>
        )}

        {edit && (
          <Box mb="2.5rem">
            <EditForm
              edit={edit}
              onSubmit={() => {
                setEdit(null);
                getWatchdogs();
              }}
              onCancel={() => {
                setEdit(null);
              }}
            />
          </Box>
        )}

        {!isEmpty(mappedWatchdogs.activeWatchdogs) && (
          <>
            <Heading
              data-test="myDogsTitle"
              as="h2"
              variant="h3"
              mb={10}
              textAlign="center"
            >
              Moji Hlídací psi
            </Heading>
            <Stack
              data-test="myDogComponentSingle"
              __css={{
                ">*": {
                  // All elements
                  w: isMobile ? "90%" : "70%",
                },
                ">*:not(:first-of-type)": {
                  // Ignore first element
                  marginTop: "30px",
                },
                ">*:last-of-type": {
                  marginBottom: "30px",
                },
              }}
              mobile={{ spacing: "15px" }}
              align="center"
            >
              {mappedWatchdogs.activeWatchdogs.map((watchdog) => (
                <WatchDog
                  deleteDog={() => handleDeleteWatchdog(watchdog.id)}
                  setEdit={() => {
                    setEdit(watchdog);
                  }}
                  key={watchdog.id}
                  wd={watchdog}
                />
              ))}
            </Stack>
          </>
        )}

        {!isEmpty(mappedWatchdogs.stoppedWatchdogs) && (
          <>
            <Divider
              borderColor="ud-grey-400"
              position="relative"
              my="2rem"
              _after={{
                // eslint-disable-next-line @typescript-eslint/quotes
                content: '"Spící Hlídací psi"',
                color: "ud-grey-800",
                fontSize: "15px",
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                padding: "0 16px",
                background: "ud-white",
              }}
            />
            <Stack
              __css={{
                ">*": {
                  // All elements
                  w: isMobile ? "90%" : "70%",
                },
                ">*:not(:first-of-type)": {
                  // Ignore first element
                  marginTop: "30px",
                },
              }}
              mobile={{ spacing: "15px" }}
              align="center"
            >
              {mappedWatchdogs.stoppedWatchdogs.map((watchdog) => (
                <WatchDog
                  deleteDog={() => handleDeleteWatchdog(watchdog.id)}
                  setEdit={() => setEdit(watchdog)}
                  key={watchdog.id}
                  wd={watchdog}
                  activateDog={() => activateDog(watchdog.id)}
                />
              ))}
            </Stack>
          </>
        )}
      </Box>
    </>
  );
};

export default WatchdogPage;
